<template>
  <div>
    <div
      class="flex flex-row flex-wrap justify-between w-full p-4 overflow-hidden bg-white rounded shadow athlete-team-card"
      ref="card"
      :class="{'expanded': isViewingMoreCoaches}"
    >
      <div class="w-3/4 pr-3 md:w-1/3 lg:w-3/4 lg:w-auto">
        <a
          :href="url"
          class="mb-4 font-semibold ds-text-xl"
          v-if="isIntegrated && showLinks"
        >{{fullName}}</a>
        <p href="#" class="mb-4 font-semibold ds-text-xl" v-else>{{fullName}}</p>

        <AthleteField label="location" v-if="city || state">
          <p class="m-0 mb-1 font-normal text-gray-900 ds-text-base">
            {{city}}
            <span v-if="city && state">,</span>
            {{state}}
          </p>
        </AthleteField>
      </div>
      <!-- Coach Information -->
      <div
        class="flex-1 hidden w-1/3 px-3 md:block lg:hidden"
        v-for="affiliation in visibleStaffAffiliations.slice(0,2)"
        :key="affiliation.firstName + affiliation.staff.lastName + Math.random()"
      >
        <StaffItem :affiliation="affiliation" />
      </div>
      <div class="hidden" v-if="visibleStaffAffiliations && visibleStaffAffiliations.length < 2"></div>
      <!-- Jersey Number -->
      <div class="flex justify-end w-16 pl-3 md:w-1/6 lg:w-16">
        <AthleteJersey :number="jerseyNumber" class="w-16" />
      </div>
      <div
        class="flex flex-col justify-end w-full md:hidden lg:flex"
        v-for="affiliation in visibleStaffAffiliations.slice(0,1)"
        :key="affiliation.staff.firstName + affiliation.staff.lastName + Math.random()"
      >
        <StaffItem :affiliation="affiliation" />
      </div>
      <SlideTransition>
        <div v-if="isViewingMoreCoaches" class="w-full">
          <StaffItem
            v-for="affiliation in visibleStaffAffiliations.slice(2)"
            :key="affiliation.staff.firstName + Math.random()"
            class="hidden w-1/4 md:block lg:hidden"
            :affiliation="affiliation"
          />

          <StaffItem
            v-for="affiliation in visibleStaffAffiliations.slice(1)"
            :key="affiliation.staff.firstName + Math.random()"
            :affiliation="affiliation"
            class="block w-full md:hidden lg:block"
          />
        </div>
      </SlideTransition>

      <a
        href="#"
        @click.prevent="toggleMoreCoaches"
        class="flex flex-row justify-between w-full mt-1 font-normal ds-text-sm md:mt-3 lg:mt-1"
        v-if="visibleStaffAffiliations.length > staffLimit"
      >
        <div class="flex flex-col justify-center">
          <span v-if="isViewingMoreCoaches">See less coaches</span>
          <span v-else>See more coaches</span>
        </div>
        <div class="flex flex-col justify-center">
          <i class="sr-2 sr-v2-chevron" :class="{'flip': isViewingMoreCoaches}" />
        </div>
      </a>
    </div>
  </div>
</template>

<script>
import AthleteField from "@/components/AthleteProfile/AthleteField";
import { SlideTransition } from '@sportsrecruits/playbook';
import AthleteJersey from "@/components/AthleteProfile/AthleteJersey";
import AthleteSectionSubheading from "@/components/AthleteProfile/AthleteSectionSubheading";
import StaffItem from "./Card/StaffItem";
import { mapGetters } from "vuex";
import makeAbsoluteUrl from "@/utils/makeAbsoluteUrl";

export default {
  components: {
    AthleteField,
    SlideTransition,
    AthleteJersey,
    AthleteSectionSubheading,
    StaffItem
  },
  props: {
    affiliation: { type: Object, required: true },
    showLinks: { required: false, default: true, type: Boolean }
  },
  mounted() {
    setTimeout(this.fixHeight, 500);
    window.addEventListener("resize", this.fixHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.fixHeight);
  },
  data() {
    return {
      isViewingMoreCoaches: false
    };
  },
  computed: {
    ...mapGetters("userData", ["isLoggedIn"]),
    ...mapGetters("athleteProfile", ["teamAffiliations"]),

    isIntegrated() {
      return !!this.affiliation.id;
    },

    url() {
      return makeAbsoluteUrl(this.team
        ? `/organization/${this.organization.urlSlug}/${this.team.urlSlug}`
        : `/organization/${this.organization.urlSlug}`);
    },

    city() {
      return (
        (this.team && this.team.city) ||
        (this.organization && this.organization.city)
      );
    },

    state() {
      return (
        (this.team && this.team.state) ||
        (this.organization && this.organization.state)
      );
    },

    jerseyNumber() {
      return this.teamJerseyNumber || this.organizationJerseyNumber;
    },

    teamJerseyNumber() {
      return this.teamAffiliation && this.teamAffiliation.jerseyNumber;
    },

    organizationJerseyNumber() {
      return (
        this.organizationAffiliation &&
        this.organizationAffiliation.jerseyNumber
      );
    },

    allStaffAffiliations() {
      return this.isIntegrated
        ? (this.team && this.team.staffAffiliations) || []
        : (this.affiliation.staff || []).map(staff => ({ staff }));
    },

    visibleStaffAffiliations() {
      if (!this.isLoggedIn) {
        return [];
      }

      const all = this.allStaffAffiliations;

      return this.isIntegrated
        ? all.filter(({ staff }) => staff.shownOnAthleteProfiles)
        : all;
    },

    fullName() {
      return (
        [this.teamName, this.organizationName].filter(n => n).join(", ") ||
        this.genericName
      );
    },

    teamAffiliation() {
      return this.isIntegrated
        ? this.teamAffiliations.find(aff => this.matchesAffiliation(aff))
        : this.affiliation;
    },

    team() {
      return this.teamAffiliation && this.teamAffiliation.team;
    },

    teamName() {
      return this.team && this.team.name;
    },

    organizationAffiliation() {
      return this.affiliation;
    },

    organization() {
      return this.affiliation.organization;
    },

    organizationName() {
      return (this.organization && this.organization.name) || null;
    },

    genericName() {
      return (
        {
          club: "Club Team",
          highSchool: "High School Team",
          additional: "Additional Team"
        }[this.affiliation.type] || "Team"
      );
    },

    staffLimit() {
      return this.$mq === "md" ? 2 : 1;
    }
  },

  methods: {
    fixHeight() {
      var self = this;
      this.$refs.card.style.height = "100%";
      if (self.$refs.card.clientHeight === 0) {
        setTimeout(this.fixHeight, 100);
      } else {
        self.$refs.card.style.height = `${self.$refs.card.clientHeight}px`;
      }
    },
    toggleMoreCoaches() {
      this.isViewingMoreCoaches = !this.isViewingMoreCoaches;
    },
    matchesAffiliation(team) {
      if (this.affiliation.type === "teamAffiliations") {
        return team.id == this.affiliation.id;
      }
      return this.isIntegrated && team.team.organization.id === this.organization.id;
    }
  }
};
</script>

<style scoped>
.flip {
  transform: rotateX(180deg);
}

@media screen and (min-width: 1024px) {
  .athlete-team-card {
    /* height: 16.25rem; */
    height: 100%;
  }
}

.athlete-team-card.expanded {
  height: auto !important;
}
</style>
